export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Axis-aligned solid block. The floor is a box too, so edges are real edges. */
export interface ArenaBox {
  id: string;
  min: Vec3;
  max: Vec3;
  /** Rendering hint only. */
  kind: "floor" | "platform" | "pillar" | "wall" | "hatch";
  /** A ghost floats through it (the museum's furniture, D49); walls and floors are never passable. */
  passable?: true;
}

/**
 * A trap of the map (D38): pulling one of its levers (hitting it with an
 * attack) warns, then opens its hatches for a moment. While open, the hatch
 * solids are gone: whoever stands on them falls into the pit (ring-out).
 */
export interface ArenaTrap {
  id: string;
  /** Where the levers stand on the floor; an attack that reaches one pulls it. */
  levers: Vec3[];
  /** Ids of the `hatch` solids it opens. */
  hatches: string[];
}

/**
 * An object of the map (D39): an obstacle, something to break, something to
 * pick up and throw. A blocking object is also a solid (`propSolid`); the
 * server removes it from the floor while it is broken.
 */
export interface ArenaProp {
  id: string;
  /** What it looks like on the client (`masso`, `colonna`, `braciere`, `sasso`…). */
  kind: string;
  /** Centre of its base. */
  x: number;
  y: number;
  z: number;
  /** Size of its box, in metres. */
  size: Vec3;
  /** Stops fighters and shots (it is a solid). */
  blocks: boolean;
  /** Hits that break it; 0 = it cannot be broken. */
  hp: number;
  /** Small objects that can be picked up (B) and thrown; never blocking. */
  carry: boolean;
  /** Seconds before it returns; zero keeps the ruins until the round resets. */
  respawn: number;
  /** A telegraphed, server-authoritative collapse before this prop breaks. */
  collapse?: {warningTicks:number;length:number;width:number;damage:number;dx:number;dz:number};
}

/** Id of the solid of a blocking object. */
export function propSolidId(propId: string): string {
  return `prop:${propId}`;
}

/** The solid of a blocking object. */
export function propSolid(p: ArenaProp): ArenaBox {
  return {
    id: propSolidId(p.id),
    kind: "pillar",
    min: { x: p.x - p.size.x / 2, y: p.y, z: p.z - p.size.z / 2 },
    max: { x: p.x + p.size.x / 2, y: p.y + p.size.y, z: p.z + p.size.z / 2 },
  };
}

/** An oval arena (amphitheatre): sand ellipse, moat around it, wall beyond. Drives the themed look. */
export interface ArenaOval {
  /** Semi-axes of the sand, in metres (x, z). */
  a: number;
  b: number;
  /** Width of the moat between the sand and the wall. */
  moat: number;
  /** Height of the wall above the sand. */
  wall: number;
}

export interface SpawnPoint {
  x: number;
  y: number;
  z: number;
}

import { teamSpawnSlot } from "./teams.ts";

export interface ArenaDefinition {
  id: string;
  schemaVersion: 1;
  displayName: string;
  solids: ArenaBox[];
  spawnPoints: SpawnPoint[];
  /**
   * Team matches (D29): one side of the arena per team, Blue then Red. If
   * missing, the teams alternate on `spawnPoints`.
   */
  teamSpawns?: [SpawnPoint[], SpawnPoint[]];
  /** Themed look of the client ("colosseo"); without it the arena is drawn box by box. */
  theme?: "colosseo" | "inferno";
  oval?: ArenaOval;
  traps?: ArenaTrap[];
  /** Objects of the map; the blocking ones must also be in `solids` (`propSolid`). */
  props?: ArenaProp[];
  /** Where the crowd may throw objects: points on the floor, not on a hatch. */
  dropSpots?: SpawnPoint[];
  /** Falling below this height respawns the fighter. */
  killY: number;
  camera: {
    /** Default yaw in radians (0 = looking towards +Z). */
    yaw: number;
    pitch: number;
    distance: number;
  };
}

export const ARENA_SCHEMA_VERSION = 1;

/** Returns a list of problems; empty means the definition is usable. */
export function validateArena(def: ArenaDefinition): string[] {
  const errors: string[] = [];
  if (def.schemaVersion !== ARENA_SCHEMA_VERSION) {
    errors.push(`schemaVersion ${String(def.schemaVersion)} non supportata`);
  }
  if (!def.id) errors.push("id mancante");
  if (def.solids.length === 0) errors.push("nessun solido");
  if (def.spawnPoints.length === 0) errors.push("nessuno spawn point");

  const ids = new Set<string>();
  for (const box of def.solids) {
    if (ids.has(box.id)) errors.push(`solido duplicato: ${box.id}`);
    ids.add(box.id);
    if (!(box.min.x < box.max.x && box.min.y < box.max.y && box.min.z < box.max.z)) {
      errors.push(`solido ${box.id}: min deve essere < max su ogni asse`);
    }
  }

  const spawns = [
    ...def.spawnPoints.map((p, i) => ({ p, label: `spawn ${i}` })),
    ...(def.teamSpawns ?? []).flatMap((side, t) => side.map((p, i) => ({ p, label: `spawn squadra ${t + 1}.${i}` }))),
  ];
  if (def.teamSpawns && def.teamSpawns.some((side) => side.length === 0)) errors.push("teamSpawns: una squadra senza spawn");
  spawns.forEach(({ p: spawn, label }) => {
    const support = groundHeightAt(def, spawn.x, spawn.z, spawn.y + 0.01);
    if (support === null || Math.abs(support - spawn.y) > 0.01) {
      errors.push(`${label}: non poggia su un solido`);
    }
    if (def.solids.some((box) => pointInsideBox(box, spawn))) {
      errors.push(`${label}: dentro un solido`);
    }
    if (spawn.y <= def.killY) errors.push(`${label}: sotto killY`);
  });

  for (const trap of def.traps ?? []) {
    if (trap.levers.length === 0) errors.push(`trappola ${trap.id}: nessuna leva`);
    for (const id of trap.hatches) {
      const box = def.solids.find((b) => b.id === id);
      if (!box) errors.push(`trappola ${trap.id}: botola ${id} inesistente`);
      else if (box.kind !== "hatch") errors.push(`trappola ${trap.id}: ${id} non è una botola`);
    }
  }
  const propIds = new Set<string>();
  for (const prop of def.props ?? []) {
    if (propIds.has(prop.id)) errors.push(`oggetto duplicato: ${prop.id}`);
    propIds.add(prop.id);
    if (prop.blocks && prop.carry) errors.push(`oggetto ${prop.id}: non può bloccare ed essere raccolto`);
    if (prop.blocks && !def.solids.some((b) => b.id === propSolidId(prop.id))) errors.push(`oggetto ${prop.id}: manca il solido`);
    if (!(prop.hp >= 0 && prop.hp <= 255 && Number.isInteger(prop.hp))) errors.push(`oggetto ${prop.id}: hp fuori da 0–255`);
    if (!prop.blocks) {
      const support = groundHeightAt(def, prop.x, prop.z, prop.y + 0.01);
      if (support === null || Math.abs(support - prop.y) > 0.01) errors.push(`oggetto ${prop.id}: non poggia su un solido`);
    }
  }
  const hatches = new Set((def.traps ?? []).flatMap((t) => t.hatches));
  for (const spot of [...def.spawnPoints, ...(def.teamSpawns ?? []).flat(), ...(def.dropSpots ?? [])]) {
    const onHatch = def.solids.some((b) => hatches.has(b.id) && spot.x >= b.min.x - 0.5 && spot.x <= b.max.x + 0.5 && spot.z >= b.min.z - 0.5 && spot.z <= b.max.z + 0.5);
    if (onHatch) errors.push(`punto (${spot.x}, ${spot.z}) sopra o accanto a una botola`);
  }
  for (const [i, spot] of (def.dropSpots ?? []).entries()) {
    const support = groundHeightAt(def, spot.x, spot.z, spot.y + 0.01);
    if (support === null || Math.abs(support - spot.y) > 0.01) errors.push(`drop ${i}: non poggia su un solido`);
  }

  return errors;
}

const variants = new WeakMap<ArenaDefinition, Map<string, ArenaDefinition>>();
const MAX_VARIANTS = 64;

/**
 * The arena as the simulation sees it right now: without the hatches while
 * they are open and without the broken objects (ids of the props). The same
 * object for the same situation, so it can be passed at every step for free.
 */
export function liveArena(def: ArenaDefinition, hatchesOpen: boolean, brokenProps: readonly string[] = []): ArenaDefinition {
  const hatches = hatchesOpen ? (def.traps ?? []).flatMap((t) => t.hatches) : [];
  if (hatches.length === 0 && brokenProps.length === 0) return def;
  const key = `${hatches.length > 0 ? "H" : ""}|${[...brokenProps].sort().join(",")}`;
  let cache = variants.get(def);
  if (!cache) {
    cache = new Map();
    variants.set(def, cache);
  }
  let live = cache.get(key);
  if (!live) {
    const gone = new Set([...hatches, ...brokenProps.map(propSolidId)]);
    live = { ...def, solids: def.solids.filter((b) => !gone.has(b.id)) };
    if (cache.size >= MAX_VARIANTS) cache.clear();
    cache.set(key, live);
  }
  return live;
}

/** `liveArena` with only the hatches. */
export function arenaWithHatches(def: ArenaDefinition, hatchesOpen: boolean): ArenaDefinition {
  return liveArena(def, hatchesOpen);
}

/** The spawn point of a spawn index: a free-for-all spot, or a team spot (`spawnIndexForTeam`). */
export function spawnPointOf(def: ArenaDefinition, index: number): SpawnPoint | undefined {
  const team = teamSpawnSlot(index);
  if (!team) return def.spawnPoints[index % def.spawnPoints.length];
  const side = def.teamSpawns?.[team.team - 1];
  if (side && side.length > 0) return side[team.slot % side.length];
  return def.spawnPoints[(team.slot * 2 + team.team - 1) % def.spawnPoints.length];
}

/**
 * Top of the highest solid under (x, z) that is not above `maxY`, or null
 * over the void. Used for blob shadows and spawn validation.
 */
export function groundHeightAt(def: ArenaDefinition, x: number, z: number, maxY: number): number | null {
  let best: number | null = null;
  for (const box of def.solids) {
    if (x < box.min.x || x > box.max.x || z < box.min.z || z > box.max.z) continue;
    if (box.max.y > maxY) continue;
    if (best === null || box.max.y > best) best = box.max.y;
  }
  return best;
}

function pointInsideBox(box: ArenaBox, p: Vec3): boolean {
  return (
    p.x > box.min.x && p.x < box.max.x &&
    p.y >= box.min.y && p.y < box.max.y - 0.001 &&
    p.z > box.min.z && p.z < box.max.z
  );
}
